import React, { useCallback, useContext, useMemo } from 'react';
import { EditorContext, FloatingMenu, BubbleMenu, useCurrentEditor, useEditor, EditorContent } from '@tiptap/react';
import { Content, Pane, Theme } from '@leverege/ui-elements';
import { Toolbar } from '@leverege/ui-plugin';
import { useValueChange } from '@leverege/ui-hooks';
import Model from "../CustomTextDetailModel.js";
import useExtensions from "./extensions/useExtensions.js";
import S from './CustomTextEditor.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function MenuBar({
  matchContext,
  context: propsContext,
  prefer
}) {
  const {
    editor
  } = useCurrentEditor();
  if (!editor) {
    return null;
  }
  const context = {
    editor,
    ...propsContext
  };
  return /*#__PURE__*/_jsx(Pane, {
    variant: "customTextEditorMenu",
    children: /*#__PURE__*/_jsx(Toolbar, {
      prefer: prefer,
      variant: "customTextMenu|customText",
      hideIfEmpty: true,
      matchContext: matchContext,
      context: context
    })
  });
}
function Bubble({
  context: propsContext
}) {
  const {
    editor
  } = useCurrentEditor();
  if (!editor) {
    return null;
  }
  const context = {
    editor,
    ...propsContext
  };
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(BubbleMenu, {
      shouldShow: options => {
        const {
          to,
          from
        } = options;
        return to !== from;
      },
      tippyOptions: {
        maxWidth: 'none'
      },
      editor: editor,
      children: /*#__PURE__*/_jsx(Pane, {
        variant: "customTextBubble|customTextEditorMenu",
        children: /*#__PURE__*/_jsx(Toolbar, {
          hideIfEmpty: true,
          variant: "customTextBubble|customText",
          matchContext: {
            use: 'BubbleMenu',
            client: 'CustomTextModelEditor'
          },
          context: context
        })
      })
    })
  });
}
function Floating({
  context: propsContext
}) {
  const {
    editor
  } = useCurrentEditor();
  if (!editor) {
    return null;
  }
  const context = {
    editor,
    ...propsContext
  };
  return /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsx(FloatingMenu, {
      editor: editor,
      tippyOptions: {
        maxWidth: 'none'
      },
      children: /*#__PURE__*/_jsx(Pane, {
        variant: "customTextEditorFloating|customTextEditorMenu",
        children: /*#__PURE__*/_jsx(Toolbar, {
          hideIfEmpty: true,
          variant: "customTextFloating|customText",
          matchContext: {
            use: 'FloatingMenu',
            client: 'CustomTextModelEditor'
          },
          context: context
        })
      })
    })
  });
}
function CustomTextEditor(props) {
  const {
    value,
    eventData,
    ...rest
  } = props;
  const onModelChange = useValueChange(Model, props);
  const extensions = useExtensions();
  const onUpdate = useCallback(({
    editor
  }) => {
    const html = editor.getHTML();
    onModelChange({
      value: html,
      data: 'setHtml'
    });
  }, [onModelChange]);
  const theme = useContext(Theme.Context);
  const editor = useEditor({
    extensions,
    content: Model.getHtml(value),
    editorProps: {
      ...rest,
      theme
    },
    onUpdate
  });
  const contextValue = useMemo(() => {
    return {
      editor
    };
  }, [editor]);
  if (!editor) {
    return null;
  }
  return /*#__PURE__*/_jsxs(EditorContext.Provider, {
    value: contextValue,
    children: [/*#__PURE__*/_jsxs(Content, {
      children: [/*#__PURE__*/_jsx(Content.Header, {
        children: /*#__PURE__*/_jsx(MenuBar, {
          prefer: "icon",
          matchContext: {
            use: 'MenuBar',
            client: 'CustomTextModelEditor'
          },
          context: {
            ...props
          }
        })
      }), /*#__PURE__*/_jsx(Content.Area, {
        children: /*#__PURE__*/_jsx(EditorContext.Consumer, {
          children: ({
            editor: currentEditor
          }) => {
            return /*#__PURE__*/_jsx(EditorContent, {
              className: S.editor,
              editor: currentEditor
            });
          }
        })
      }), /*#__PURE__*/_jsx(Content.Footer, {
        children: /*#__PURE__*/_jsx(MenuBar, {
          prefer: "icon",
          matchContext: {
            use: 'PostMenuBar',
            client: 'CustomTextModelEditor'
          },
          context: {
            ...props
          }
        })
      })]
    }), /*#__PURE__*/_jsx(Bubble, {
      context: {
        ...props
      }
    }), /*#__PURE__*/_jsx(Floating, {
      context: {
        ...props
      }
    })]
  });
}
export default CustomTextEditor;